/**
 * Generic profile panel for OIDC and local auth modes.
 *
 * Displays user info and (for local mode) a password-change form.
 */

import React, { useState, FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { isLocalAuth } from '../../config/auth';

const GenericProfilePanel: React.FC = () => {
  const { user, getAccessToken } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChangePassword = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getAccessToken();
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBase}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Failed to change password');
      }

      setMessage({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 p-6 max-w-lg">
      {/* User info */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-ui-text-primary">Profile</h3>
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-ui-text-tertiary">Name: </span>
            <span className="text-ui-text-primary">{user?.name || '—'}</span>
          </div>
          <div>
            <span className="text-ui-text-tertiary">Email: </span>
            <span className="text-ui-text-primary">{user?.email || '—'}</span>
          </div>
          <div>
            <span className="text-ui-text-tertiary">Username: </span>
            <span className="text-ui-text-primary">{user?.username || '—'}</span>
          </div>
        </div>
      </section>

      {/* Password change (local only) */}
      {isLocalAuth && (
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-ui-text-primary">Change password</h3>

          {message && (
            <div
              className={`rounded-md p-3 text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-3">
            <input
              type="password"
              required
              placeholder="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
            />
            <input
              type="password"
              required
              minLength={8}
              placeholder="New password (min. 8 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
            />
            <input
              type="password"
              required
              minLength={8}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-lime-500 hover:bg-lime-600 disabled:opacity-50 text-white font-medium px-4 py-2 text-sm transition-colors"
            >
              {isSubmitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </section>
      )}
    </div>
  );
};

export default GenericProfilePanel;
